import { useEffect, useMemo, useState } from "react"
import { HttpMethodBadge } from "./http-method-badge"
import { Badge } from "@/components/ui/badge"
import type { Endpoint, TestInvokeResult } from "@/types/endpoint"

type Props = {
  endpoint: Endpoint | null
  defaultSourceKey: string | null
  csrfToken: string
}

function statusTone(status: number | null): "success" | "warning" | "error" | "neutral" {
  if (status == null) return "error"
  if (status >= 200 && status < 300) return "success"
  if (status >= 300 && status < 400) return "neutral"
  if (status >= 400 && status < 500) return "warning"
  return "error"
}

export function EndpointTestPanel({ endpoint, defaultSourceKey, csrfToken }: Props) {
  const [sourceKey, setSourceKey] = useState(endpoint?.testSourceKey ?? defaultSourceKey ?? "")
  const [queryValues, setQueryValues] = useState<Record<string, string>>({})
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<TestInvokeResult | null>(null)
  const [tab, setTab] = useState<"body" | "headers">("body")
  const [curlLoading, setCurlLoading] = useState(false)
  const [curlStatus, setCurlStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null)

  // Reset the source_key field whenever the operator selects a different
  // endpoint — each endpoint remembers its own last-tested key.
  useEffect(() => {
    if (!endpoint) return
    setSourceKey(endpoint.testSourceKey ?? defaultSourceKey ?? "")
    setResult(null)
    setQueryValues({})
  }, [endpoint?.id])

  const interpolatedPath = useMemo(() => {
    if (!endpoint) return ""
    const filled = endpoint.pathTemplate.replace("{source_key}", encodeURIComponent(sourceKey || "{source_key}"))
    const qs = Object.entries(queryValues)
      .filter(([, v]) => v != null && String(v).trim() !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&")
    return qs ? `${filled}?${qs}` : filled
  }, [endpoint?.pathTemplate, sourceKey, queryValues])

  const missingRequired = useMemo(() => {
    if (!endpoint) return false
    return endpoint.querySchema.some(
      (p) => p.required && !(queryValues[p.name] && queryValues[p.name].trim()),
    )
  }, [endpoint?.querySchema, queryValues])

  if (!endpoint) {
    return (
      <div className="flex h-full items-center justify-center rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        Select an endpoint to test it.
      </div>
    )
  }

  const handleRun = async () => {
    setRunning(true)
    try {
      const resp = await fetch(`/endpoints/${endpoint.id}/test_invoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({ source_key: sourceKey, query: queryValues }),
      })
      const json = await resp.json()
      setResult(json)
    } catch (err) {
      setResult({
        status: null, statusLabel: "Network error",
        body: null, error: String(err),
        latencyMs: 0, requestPath: endpoint.pathTemplate, requestUrl: null, requestMethod: endpoint.httpMethod,
        responseHeaders: {}
      })
    } finally {
      setRunning(false)
    }
  }

  const copyBody = () => {
    if (result?.body == null) return
    const text = typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2)
    navigator.clipboard?.writeText(text)
  }

  const copyCurl = async () => {
    if (!endpoint) return
    setCurlLoading(true)
    setCurlStatus(null)
    try {
      const resp = await fetch(`/endpoints/${endpoint.id}/curl_command`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({ source_key: sourceKey, query: queryValues }),
      })
      const json = await resp.json()
      if (typeof json.curl !== "string") {
        setCurlStatus({ kind: "err", msg: "Failed to build curl" })
        return
      }
      await navigator.clipboard?.writeText(json.curl)
      setCurlStatus({ kind: "ok", msg: "Copied — paste into a shell. Treat as a secret." })
    } catch (err) {
      setCurlStatus({ kind: "err", msg: String(err) })
    } finally {
      setCurlLoading(false)
    }
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="rounded-card border border-border bg-app-panel">
        <div className="border-b border-border p-4">
          <div className="text-sm font-medium text-app-text">Test Endpoint</div>
          <div className="text-xs text-app-muted">Provide a source key and test this endpoint.</div>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Source Key</label>
            <input type="text" value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}
              placeholder="usr_…"
              className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none" />
            <p className="mt-1 text-xs text-app-muted">Defaults to the latest appuser source_key for this company.</p>
          </div>

          {endpoint.querySchema.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-app-muted">Query Params</div>
              {endpoint.querySchema.map((p) => (
                <div key={p.name}>
                  <label className="mb-1 block font-mono text-xs text-app-muted">
                    {p.name}{p.required && <span className="ml-1 text-status-error">*</span>}
                  </label>
                  <input
                    type="text"
                    value={queryValues[p.name] ?? ""}
                    onChange={(e) => setQueryValues((q) => ({ ...q, [p.name]: e.target.value }))}
                    placeholder={p.required ? "required" : "optional"}
                    className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 rounded-control border border-border bg-app-bg px-2 py-2">
            <HttpMethodBadge method={endpoint.httpMethod} />
            <span className="truncate font-mono text-xs text-app-text">{interpolatedPath}</span>
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={handleRun} disabled={running || !sourceKey.trim() || missingRequired}
              className="flex-1 rounded-control border border-brand-red bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-redDark disabled:opacity-60">
              {running ? "Sending…" : "▶ Test Endpoint"}
            </button>
            <button type="button" onClick={copyCurl} disabled={curlLoading || !sourceKey.trim()}
              className="flex-1 rounded-control border border-border bg-transparent px-4 py-2 text-sm font-medium text-app-text transition hover:bg-white/5 disabled:opacity-60"
              title="Build a terminal curl command (with auth headers) and copy it to your clipboard">
              {curlLoading ? "Building…" : "Copy Curl"}
            </button>
          </div>
          {curlStatus && (
            <div className={`text-xs ${curlStatus.kind === "ok" ? "text-status-success" : "text-status-error"}`}>
              {curlStatus.msg}
            </div>
          )}
        </div>
      </div>

      {result && (
        <div className="rounded-card border border-border bg-app-panel">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="text-sm font-medium text-app-text">Response</div>
            <div className="flex items-center gap-2 text-xs">
              <span className={`h-2 w-2 rounded-full ${result.status && result.status >= 200 && result.status < 300 ? "bg-status-success" : "bg-status-error"}`} />
              <span className="text-app-muted">Completed in {result.latencyMs}ms</span>
            </div>
          </div>

          <div className="border-b border-border p-4">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-semibold text-app-text">{result.status ?? "—"}</div>
              <div className="space-y-0.5">
                <Badge tone={statusTone(result.status)}>{result.statusLabel ?? "—"}</Badge>
                {result.error && <div className="text-xs text-status-error">{result.error}</div>}
              </div>
            </div>
            {result.requestUrl && (
              <div className="mt-3 flex items-center gap-2 rounded-control border border-border bg-app-bg px-2 py-2">
                <HttpMethodBadge method={result.requestMethod} />
                <code className="flex-1 truncate font-mono text-xs text-app-text" title={result.requestUrl}>
                  {result.requestUrl}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(result.requestUrl ?? "")}
                  className="rounded-control border border-border px-2 py-0.5 text-[11px] text-app-muted hover:text-app-text"
                  title="Copy URL"
                >
                  Copy
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 border-b border-border px-4">
            <button type="button" onClick={() => setTab("body")}
              className={`border-b-2 px-3 py-2 text-xs ${tab === "body" ? "border-brand-red text-app-text" : "border-transparent text-app-muted"}`}>
              Response Body
            </button>
            <button type="button" onClick={() => setTab("headers")}
              className={`border-b-2 px-3 py-2 text-xs ${tab === "headers" ? "border-brand-red text-app-text" : "border-transparent text-app-muted"}`}>
              Headers
            </button>
            <div className="ml-auto">
              {tab === "body" && result.body != null && (
                <button type="button" onClick={copyBody}
                  className="rounded-control border border-border px-2 py-1 text-xs text-app-muted hover:text-app-text">
                  Copy
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[40vh] overflow-auto p-4">
            {tab === "body" ? (
              <pre className="whitespace-pre-wrap break-words font-mono text-xs text-app-text">
                {result.body == null ? "(no body)" :
                 typeof result.body === "string" ? result.body :
                 JSON.stringify(result.body, null, 2)}
              </pre>
            ) : (
              Object.keys(result.responseHeaders ?? {}).length === 0 ? (
                <div className="font-mono text-xs text-app-muted">
                  (no response headers — request never reached the partner)
                </div>
              ) : (
                <table className="w-full font-mono text-[11px]">
                  <tbody className="divide-y divide-border">
                    {Object.entries(result.responseHeaders).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                      <tr key={k}>
                        <td className="whitespace-nowrap pr-3 py-1 align-top text-app-muted">{k}</td>
                        <td className="break-all py-1 text-app-text">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      )}

      {endpoint.errorStats.last30dCount > 0 && (
        <div className="rounded-card border border-border bg-app-panel p-4 text-xs text-app-muted">
          {endpoint.errorStats.last30dCount} errors in last 30 days · {endpoint.errorStats.last24hCount} in last 24h. Click the row sparkline for details.
        </div>
      )}
    </div>
  )
}

